import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../cn";
import { Button } from "./button";
import { Card, CardBody, CardDescription, CardHeader, CardTitle } from "./card";

/**
 * One thing the platform refused, and what to do about it.
 *
 * The 422 envelope carries every refusal at once by design, because somebody
 * fixing four problems one publish at a time stops trusting the check. All of
 * them are drawn, never the first.
 */
export interface ErrorProblem {
  /** What it is about, in the reader's words. */
  subject: string;
  /** What is wrong with it. */
  message: string;
  /** What to do — the part that makes the refusal actionable. */
  fix?: string | null;
  /** Where it is, for somebody who will go and edit the file. */
  path?: string | null;
}

export interface ErrorStateProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  /**
   * What could not be done, as a sentence a non-developer can act on.
   *
   * Optional, because most failures lead with the message the server sent. When
   * a screen knows better than the wire does — *This game's overview could not
   * be read* — that sentence goes here and the server's goes in `message`.
   */
  title?: ReactNode;
  /** The refusal itself. */
  message: ReactNode;
  /** The named fix, when the server gave one. */
  hint?: ReactNode;
  /** Every refusal the envelope carried. */
  problems?: readonly ErrorProblem[];
  /** The one piece of information that turns "it's broken" into something answerable. */
  requestId?: string;
  /** Retrying. Absent when trying again is not the fix, and then there is no button. */
  onRetry?: () => void;
  /** A second way on — *Open Requests*, say. */
  children?: ReactNode;
  /**
   * Classes for each part, which win over its defaults: a shell that draws this
   * card at its own density restyles the parts without forking the card.
   * Opt-in; without it nothing changes.
   */
  classNames?: ErrorStateClassNames;
}

/** The parts of an {@link ErrorState} a caller can add classes to. */
export interface ErrorStateClassNames {
  title?: string;
  message?: string;
  hint?: string;
  problems?: string;
  problem?: string;
  fix?: string;
  path?: string;
  /** The row holding Try again, the caller's children and the request id. */
  actions?: string;
  retry?: string;
  requestId?: string;
}

/**
 * A failure the reader can act on.
 *
 * **An error is a fourth thing, not an empty state.** It takes the critical
 * tint and a critical stripe rather than the dashed box `EmptyState` used to
 * draw, because the reader must never be told to create something when the
 * truth is that the read failed — *no leaderboards yet* and *we could not ask*
 * are opposite facts and were the same picture.
 *
 * The three parts, in order: the refusal in the reader's language, the fix
 * named, and **Try again**. The request id sits last, dim and copyable, because
 * it is the identifier and identifiers are never what a row leads with.
 *
 * It takes a flattened refusal rather than an `unknown` error on purpose: this
 * package knows about surfaces and hairlines and nothing about the API client,
 * and reaching for `@magicweave/api` from inside the design system would make
 * every screen's styling depend on a generated client. Each shell keeps the
 * thin adapter that turns its own errors into these props.
 */
export function ErrorState({
  className,
  title,
  message,
  hint,
  problems,
  requestId,
  onRetry,
  children,
  classNames = {},
  ...props
}: ErrorStateProps) {
  return (
    <div
      role="alert"
      data-testid="error"
      className={cn(
        "rounded-[var(--radius-card)] border border-l-3 border-rule border-l-critical bg-critical-soft p-5",
        className,
      )}
      {...props}
    >
      {title !== undefined ? (
        <h2
          className={cn("mb-2 text-prose font-semibold text-critical-text", classNames.title)}
        >
          {title}
        </h2>
      ) : null}
      {message !== null && message !== undefined ? (
        <p className={cn("text-prose font-medium text-ink", classNames.message)}>{message}</p>
      ) : null}
      {hint !== undefined ? (
        <p className={cn("mt-1 text-prose text-ink-2", classNames.hint)}>{hint}</p>
      ) : null}
      {problems && problems.length > 0 ? (
        <ul className={cn("mt-3 space-y-2", classNames.problems)} data-testid="problems">
          {problems.map((problem, i) => (
            <li
              key={`${problem.subject}-${String(i)}`}
              className={cn("text-ui", classNames.problem)}
            >
              <p className="text-ink">
                <span className="font-medium">{problem.subject}</span>
                <span className="text-ink-2"> — {problem.message}</span>
              </p>
              {problem.fix ? (
                <p className={cn("mt-0.5 text-ink-2", classNames.fix)}>{problem.fix}</p>
              ) : null}
              {problem.path ? (
                <code
                  className={cn("mt-0.5 block font-mono text-meta text-ink-3", classNames.path)}
                >
                  {problem.path}
                </code>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      <div className={cn("mt-4 flex flex-wrap items-center gap-3", classNames.actions)}>
        {onRetry ? (
          <Button
            size="sm"
            variant="secondary"
            icon="refresh"
            className={classNames.retry}
            onClick={onRetry}
          >
            Try again
          </Button>
        ) : null}
        {children}
        {requestId ? (
          <code
            className={cn("font-mono text-meta text-ink-3", classNames.requestId)}
            data-testid="request-id"
          >
            {requestId}
          </code>
        ) : null}
      </div>
    </div>
  );
}

export interface RefusedProps extends HTMLAttributes<HTMLDivElement> {
  /** The screen, named the way the person navigating to it named it. */
  label: string;
  /** The roles that carry the scope this screen needs. */
  roles: readonly string[];
}

/**
 * A screen this person's grant does not open.
 *
 * It names the roles that carry the scope, because the useful sentence is not
 * "you cannot" — it is "ask an admin for one of these". Staff are colleagues,
 * so the way out is a request to a person rather than a support ticket.
 *
 * It is not the enforcement, and it is not an error either. Control checks the
 * token on every request, so the same person pasting the URL of a screen this
 * hides still reaches nothing — which is what makes it safe for this to be a
 * helpful message rather than a blank page.
 *
 * A card, and the containment rule's one exception: around a genuinely
 * single-purpose screen the boundary says *this is all there is*, where an
 * unbounded sentence on an empty page reads as a page that failed to load.
 */
export function Refused({ className, label, roles, ...props }: RefusedProps) {
  return (
    <div data-testid="refused" className={cn("max-w-2xl", className)} {...props}>
      <Card>
        <CardHeader>
          <CardTitle>{label} is not part of your platform access</CardTitle>
          <CardDescription>
            Your grant does not carry it, so nothing on this screen would load.
          </CardDescription>
        </CardHeader>
        <CardBody>
          <p className="text-ui text-ink-2">
            {carriedBy(roles)} A platform admin can grant you one of those.
          </p>
        </CardBody>
      </Card>
    </div>
  );
}

/** "Support carries it." / "Support, Finance and Ops carry it." */
function carriedBy(roles: readonly string[]): string {
  if (roles.length === 0) return "No role carries it.";
  if (roles.length === 1) return `${roles[0]!} carries it.`;
  return `${roles.slice(0, -1).join(", ")} and ${roles.at(-1)!} carry it.`;
}
